import React, { useEffect, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Alert,
  Badge,
  Table,
  Modal,
} from "react-bootstrap";

import {
  ArrowLeft,
  BoxSeam,
  Image as ImageIcon,
  PlusCircle,
  Search,
  CheckCircleFill,
  ExclamationTriangleFill,
  ArrowClockwise,
  PencilSquare,
  Trash,
  XCircle,
  Save,
  BoxArrowRight,
  HouseDoor,
} from "react-bootstrap-icons";

import "./AdminProductos.css";

/* =========================================================
   API
========================================================= */

const API =
  process.env.NODE_ENV === "production"
    ? "https://gj-frio-industrial-api.onrender.com"
    : "http://localhost:4000";

/* =========================================================
   AUTENTICACIÓN
========================================================= */

const obtenerToken = () => {
  return sessionStorage.getItem("gj_admin_token");
};

const obtenerHeadersAuth = () => {
  const token = obtenerToken();

  return {
    Authorization: `Bearer ${token}`,
  };
};

/* =========================================================
   COMPONENTE
========================================================= */

export default function AdminProductos() {
  const navigate = useNavigate();

  // =====================================================
  // ESTADOS
  // =====================================================

  const [tipos, setTipos] = useState([]);

  const [tipoSeleccionado, setTipoSeleccionado] = useState("");

  const [form, setForm] = useState({});

  const [imagen, setImagen] = useState(null);

  const [preview, setPreview] = useState("");

  const [productos, setProductos] = useState([]);

  const [busqueda, setBusqueda] = useState("");

  const [cargandoConfig, setCargandoConfig] = useState(true);

  const [cargandoProductos, setCargandoProductos] = useState(false);

  const [guardando, setGuardando] = useState(false);

  // Producto que estamos editando
  const [productoEditando, setProductoEditando] = useState(null);

  // Producto que queremos eliminar
  const [productoEliminar, setProductoEliminar] = useState(null);

  const [eliminando, setEliminando] = useState(false);

  const [mensaje, setMensaje] = useState({
    tipo: "",
    texto: "",
  });

  // =====================================================
  // CERRAR SESIÓN
  // =====================================================

  const cerrarSesion = () => {
    sessionStorage.removeItem("gj_admin_token");
    sessionStorage.removeItem("gj_admin_usuario");

    navigate("/login", {
      replace: true,
    });
  };

  // =====================================================
  // CERRAR SESIÓN AL ACTUALIZAR O CERRAR LA PESTAÑA
  // =====================================================

  useEffect(() => {
    const cerrarAlSalir = () => {
      sessionStorage.removeItem("gj_admin_token");
      sessionStorage.removeItem("gj_admin_usuario");
    };

    window.addEventListener("beforeunload", cerrarAlSalir);

    return () => {
      window.removeEventListener("beforeunload", cerrarAlSalir);
    };
  }, []);

  // =====================================================
  // TIPO ACTUAL
  // =====================================================

  const tipoActual = useMemo(() => {
    return tipos.find((item) => item.tipo === tipoSeleccionado);
  }, [tipos, tipoSeleccionado]);

  // =====================================================
  // CARGAR CONFIGURACIÓN
  // =====================================================

  useEffect(() => {
    cargarConfiguracion();
  }, []);

  const cargarConfiguracion = async () => {
    try {
      setCargandoConfig(true);

      setMensaje({
        tipo: "",
        texto: "",
      });

      const response = await fetch(`${API}/admin/productos/configuracion`, {
        headers: obtenerHeadersAuth(),
      });

      // Token inválido o sesión vencida
      if (response.status === 401) {
        cerrarSesion();
        return;
      }

      if (!response.ok) {
        throw new Error("No se pudo obtener la configuración de productos.");
      }

      const data = await response.json();

      setTipos(data);

      if (data.length > 0) {
        setTipoSeleccionado(data[0].tipo);
      }
    } catch (error) {
      console.error(error);

      setMensaje({
        tipo: "danger",
        texto: "No se pudo cargar la configuración del administrador.",
      });
    } finally {
      setCargandoConfig(false);
    }
  };

  // =====================================================
  // CAMBIO DE TIPO
  // =====================================================

  useEffect(() => {
    if (!tipoSeleccionado) return;

    limpiarFormulario(false);

    cargarProductos(tipoSeleccionado);
  }, [tipoSeleccionado]);

  // =====================================================
  // CARGAR PRODUCTOS
  // =====================================================

  const cargarProductos = async (tipo = tipoSeleccionado) => {
    if (!tipo) return;

    try {
      setCargandoProductos(true);

      const response = await fetch(`${API}/admin/productos/${tipo}`, {
        headers: obtenerHeadersAuth(),
      });

      if (response.status === 401) {
        cerrarSesion();
        return;
      }

      if (!response.ok) {
        throw new Error("No se pudieron cargar los productos.");
      }

      const data = await response.json();

      setProductos(data);
    } catch (error) {
      console.error(error);

      setMensaje({
        tipo: "danger",
        texto: "No se pudo cargar el listado de productos.",
      });
    } finally {
      setCargandoProductos(false);
    }
  };

  // =====================================================
  // CAMBIO DE INPUTS
  // =====================================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // =====================================================
  // IMAGEN
  // =====================================================

  const handleImagen = (e) => {
    const archivo = e.target.files?.[0];

    if (!archivo) {
      setImagen(null);

      if (productoEditando?.imagen) {
        setPreview(productoEditando.imagen);
      } else {
        setPreview("");
      }

      return;
    }

    if (preview && preview.startsWith("blob:")) {
      URL.revokeObjectURL(preview);
    }

    setImagen(archivo);

    const url = URL.createObjectURL(archivo);

    setPreview(url);
  };

  // =====================================================
  // LIMPIAR FORMULARIO
  // =====================================================

  const limpiarFormulario = (limpiarMensaje = true) => {
    setForm({});

    setImagen(null);

    setProductoEditando(null);

    if (preview && preview.startsWith("blob:")) {
      URL.revokeObjectURL(preview);
    }

    setPreview("");

    const inputImagen = document.getElementById("admin-producto-imagen");

    if (inputImagen) {
      inputImagen.value = "";
    }

    if (limpiarMensaje) {
      setMensaje({
        tipo: "",
        texto: "",
      });
    }
  };

  // =====================================================
  // NUEVO PRODUCTO
  // =====================================================

  const nuevoProducto = () => {
    limpiarFormulario();

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =====================================================
  // EDITAR PRODUCTO
  // =====================================================

  const editarProducto = (producto) => {
    const nuevosDatos = {};

    tipoActual?.campos.forEach((campo) => {
      nuevosDatos[campo.nombre] = producto[campo.nombre] ?? "";
    });

    setForm(nuevosDatos);

    setProductoEditando(producto);

    setImagen(null);

    if (preview && preview.startsWith("blob:")) {
      URL.revokeObjectURL(preview);
    }

    setPreview(producto.imagen || "");

    const inputImagen = document.getElementById("admin-producto-imagen");

    if (inputImagen) {
      inputImagen.value = "";
    }

    setMensaje({
      tipo: "",
      texto: "",
    });

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =====================================================
  // GUARDAR / ACTUALIZAR
  // =====================================================

  const guardarProducto = async (e) => {
    e.preventDefault();

    if (!tipoSeleccionado || !tipoActual) {
      return;
    }

    // Para crear la imagen es obligatoria.
    // Para editar se puede conservar la actual.
    if (!productoEditando && !imagen) {
      setMensaje({
        tipo: "warning",
        texto: "Selecciona una imagen para el producto.",
      });

      return;
    }

    try {
      setGuardando(true);

      setMensaje({
        tipo: "",
        texto: "",
      });

      const datos = new FormData();

      tipoActual.campos.forEach((campo) => {
        const valor = form[campo.nombre];

        if (productoEditando) {
          // En edición enviamos también campos vacíos
          datos.append(campo.nombre, valor ?? "");
        } else if (valor !== undefined && valor !== "") {
          datos.append(campo.nombre, valor);
        }
      });

      // Solo enviamos imagen durante edición
      // cuando el usuario seleccionó una nueva.
      if (imagen) {
        datos.append("imagen", imagen);
      }

      const url = productoEditando
        ? `${API}/admin/productos/${tipoSeleccionado}/${productoEditando._id}`
        : `${API}/admin/productos/${tipoSeleccionado}`;

      const metodo = productoEditando ? "PUT" : "POST";

      const response = await fetch(url, {
        method: metodo,

        headers: obtenerHeadersAuth(),

        body: datos,
      });

      // Sesión vencida
      if (response.status === 401) {
        cerrarSesion();
        return;
      }

      let resultado = {};

      try {
        resultado = await response.json();
      } catch {
        resultado = {};
      }

      if (!response.ok) {
        throw new Error(
          resultado.message ||
            (productoEditando
              ? "No se pudo actualizar el producto."
              : "No se pudo guardar el producto."),
        );
      }

      const eraEdicion = Boolean(productoEditando);

      limpiarFormulario(false);

      await cargarProductos(tipoSeleccionado);

      setMensaje({
        tipo: "success",

        texto: eraEdicion
          ? "Producto actualizado correctamente."
          : "Producto guardado correctamente.",
      });
    } catch (error) {
      console.error(error);

      setMensaje({
        tipo: "danger",
        texto: error.message,
      });
    } finally {
      setGuardando(false);
    }
  };

  // =====================================================
  // ABRIR MODAL ELIMINAR
  // =====================================================

  const confirmarEliminar = (producto) => {
    setProductoEliminar(producto);
  };

  // =====================================================
  // CERRAR MODAL ELIMINAR
  // =====================================================

  const cerrarEliminar = () => {
    if (eliminando) return;

    setProductoEliminar(null);
  };

  // =====================================================
  // ELIMINAR PRODUCTO
  // =====================================================

  const eliminarProducto = async () => {
    if (!productoEliminar?._id || !tipoSeleccionado) {
      return;
    }

    try {
      setEliminando(true);

      setMensaje({
        tipo: "",
        texto: "",
      });

      const response = await fetch(
        `${API}/admin/productos/${tipoSeleccionado}/${productoEliminar._id}`,
        {
          method: "DELETE",

          headers: obtenerHeadersAuth(),
        },
      );

      // Sesión vencida
      if (response.status === 401) {
        cerrarSesion();
        return;
      }

      let resultado = {};

      try {
        resultado = await response.json();
      } catch {
        resultado = {};
      }

      if (!response.ok) {
        throw new Error(
          resultado.message || "No se pudo eliminar el producto.",
        );
      }

      // Si estamos editando justamente
      // el producto eliminado,
      // limpiamos el formulario.
      if (productoEditando?._id === productoEliminar._id) {
        limpiarFormulario(false);
      }

      setProductoEliminar(null);

      await cargarProductos(tipoSeleccionado);

      setMensaje({
        tipo: "success",
        texto: "Producto eliminado correctamente.",
      });
    } catch (error) {
      console.error(error);

      setMensaje({
        tipo: "danger",
        texto: error.message,
      });
    } finally {
      setEliminando(false);
    }
  };

  // =====================================================
  // FILTRADO
  // =====================================================

  const productosFiltrados = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();

    if (!texto) {
      return productos;
    }

    return productos.filter((producto) => {
      const nombre = String(producto.nombre || "").toLowerCase();

      const codigo = String(producto.codigoProducto || "").toLowerCase();

      const marca = String(producto.marca || "").toLowerCase();

      return (
        nombre.includes(texto) ||
        codigo.includes(texto) ||
        marca.includes(texto)
      );
    });
  }, [productos, busqueda]);

  // =====================================================
  // ETIQUETAS
  // =====================================================

  const obtenerEtiqueta = (campo) => {
    const etiquetas = {
      nombre: "Nombre del producto",

      marca: "Marca",

      modelo: "Modelo",

      codigoProducto: "Código del producto",

      descripcion: "Descripción",

      fanSize: "Tamaño del ventilador",
    };

    return (
      etiquetas[campo] ||
      campo
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (letra) => letra.toUpperCase())
    );
  };

  // =====================================================
  // LOADING INICIAL
  // =====================================================

  if (cargandoConfig) {
    return (
      <div className="admin-productos-loading">
        <Spinner animation="border" />

        <span>Cargando administrador...</span>
      </div>
    );
  }

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <div className="admin-productos-page">
      <Container fluid="xl">
        {/* =================================================
            HEADER
        ================================================= */}

        <div className="admin-productos-header">
          <div>
            <span className="admin-productos-kicker">ADMINISTRACIÓN</span>

            <h1>
              <BoxSeam />
              Administrar productos
            </h1>

            <p>
              Agrega, edita y elimina productos del catálogo desde un solo
              lugar.
            </p>
          </div>

          {/* ===============================================
              CONTADOR + CERRAR SESIÓN
          =============================================== */}

          <div className="d-flex align-items-center gap-2 flex-wrap">
            <Badge bg="primary" className="admin-productos-total">
              {productos.length} productos
            </Badge>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => navigate("/admin")}
            >
              <ArrowLeft className="me-2" />
              Volver al menú
            </Button>
            <Button
              type="button"
              variant="outline-danger"
              size="sm"
              onClick={cerrarSesion}
            >
              <BoxArrowRight className="me-2" />
              Cerrar sesión
            </Button>
          </div>
        </div>

        {/* =================================================
            MENSAJES
        ================================================= */}

        {mensaje.texto && (
          <Alert
            variant={mensaje.tipo}
            dismissible
            onClose={() =>
              setMensaje({
                tipo: "",
                texto: "",
              })
            }
          >
            {mensaje.tipo === "success" ? (
              <CheckCircleFill className="me-2" />
            ) : (
              <ExclamationTriangleFill className="me-2" />
            )}

            {mensaje.texto}
          </Alert>
        )}

        <Row className="g-4">
          {/* =================================================
              FORMULARIO
          ================================================= */}

          <Col lg={5}>
            <Card className="admin-productos-card">
              <Card.Body>
                <div className="admin-card-title">
                  <div className="admin-card-icon">
                    {productoEditando ? <PencilSquare /> : <PlusCircle />}
                  </div>

                  <div>
                    <h2>
                      {productoEditando ? "Editar producto" : "Nuevo producto"}
                    </h2>

                    <p>
                      {productoEditando
                        ? "Modifica los datos del producto seleccionado."
                        : "Selecciona el tipo y completa sus datos."}
                    </p>
                  </div>
                </div>

                {/* PRODUCTO EN EDICIÓN */}

                {productoEditando && (
                  <Alert variant="primary" className="py-2">
                    <div>
                      <strong>Editando:</strong>{" "}
                      {productoEditando.nombre || "Producto"}
                    </div>

                    <div className="small mt-1">
                      Código: {productoEditando.codigoProducto || "—"}
                    </div>
                  </Alert>
                )}

                <Form onSubmit={guardarProducto}>
                  {/* TIPO DE PRODUCTO */}

                  <Form.Group className="mb-3">
                    <Form.Label>Tipo de producto</Form.Label>

                    <Form.Select
                      value={tipoSeleccionado}
                      onChange={(e) => setTipoSeleccionado(e.target.value)}
                      disabled={Boolean(productoEditando)}
                    >
                      {tipos.map((tipo) => (
                        <option key={tipo.tipo} value={tipo.tipo}>
                          {tipo.nombre}
                        </option>
                      ))}
                    </Form.Select>

                    {productoEditando && (
                      <Form.Text className="text-muted">
                        Para cambiar de tipo, cancela primero la edición.
                      </Form.Text>
                    )}
                  </Form.Group>

                  {/* CAMPOS DINÁMICOS */}

                  {tipoActual?.campos.map((campo) => (
                    <Form.Group className="mb-3" key={campo.nombre}>
                      <Form.Label>
                        {obtenerEtiqueta(campo.nombre)}

                        {campo.requerido && (
                          <span className="campo-requerido"> *</span>
                        )}
                      </Form.Label>

                      {campo.nombre === "descripcion" ? (
                        <Form.Control
                          as="textarea"
                          rows={4}
                          name={campo.nombre}
                          value={form[campo.nombre] || ""}
                          onChange={handleChange}
                          required={campo.requerido}
                        />
                      ) : (
                        <Form.Control
                          type={campo.tipo}
                          name={campo.nombre}
                          value={form[campo.nombre] || ""}
                          onChange={handleChange}
                          required={campo.requerido}
                        />
                      )}
                    </Form.Group>
                  ))}

                  {/* =================================================
                      IMAGEN
                  ================================================= */}

                  <Form.Group className="mb-3">
                    <Form.Label>
                      Imagen del producto
                      {!productoEditando && (
                        <span className="campo-requerido"> *</span>
                      )}
                    </Form.Label>

                    <Form.Control
                      id="admin-producto-imagen"
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={handleImagen}
                    />

                    {productoEditando && (
                      <Form.Text className="text-muted">
                        Déjalo vacío para conservar la imagen actual.
                      </Form.Text>
                    )}
                  </Form.Group>

                  {/* PREVIEW */}

                  {preview ? (
                    <div className="admin-imagen-preview">
                      <img src={preview} alt="Vista previa" />
                    </div>
                  ) : (
                    <div className="admin-imagen-vacia">
                      <ImageIcon />

                      <span>Vista previa de la imagen</span>
                    </div>
                  )}

                  {/* =================================================
                      BOTONES FORMULARIO
                  ================================================= */}

                  <div className="admin-form-actions">
                    {productoEditando ? (
                      <>
                        <Button
                          type="button"
                          variant="light"
                          onClick={() => limpiarFormulario()}
                          disabled={guardando}
                        >
                          <XCircle className="me-2" />
                          Cancelar
                        </Button>

                        <Button
                          type="submit"
                          variant="primary"
                          disabled={guardando}
                        >
                          {guardando ? (
                            <>
                              <Spinner
                                animation="border"
                                size="sm"
                                className="me-2"
                              />
                              Actualizando...
                            </>
                          ) : (
                            <>
                              <Save className="me-2" />
                              Guardar cambios
                            </>
                          )}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          type="button"
                          variant="light"
                          onClick={nuevoProducto}
                          disabled={guardando}
                        >
                          Limpiar
                        </Button>

                        <Button
                          type="submit"
                          variant="primary"
                          disabled={guardando}
                        >
                          {guardando ? (
                            <>
                              <Spinner
                                animation="border"
                                size="sm"
                                className="me-2"
                              />
                              Guardando...
                            </>
                          ) : (
                            <>
                              <PlusCircle className="me-2" />
                              Guardar producto
                            </>
                          )}
                        </Button>
                      </>
                    )}
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          {/* =================================================
              LISTADO
          ================================================= */}

          <Col lg={7}>
            <Card className="admin-productos-card">
              <Card.Body>
                <div className="admin-list-header">
                  <div>
                    <h2>{tipoActual?.nombre || "Productos"}</h2>

                    <p>
                      {productos.length} producto
                      {productos.length !== 1 ? "s" : ""} registrado
                      {productos.length !== 1 ? "s" : ""}
                    </p>
                  </div>

                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => cargarProductos(tipoSeleccionado)}
                    disabled={cargandoProductos}
                    title="Actualizar listado"
                  >
                    <ArrowClockwise />
                  </Button>
                </div>

                {/* BUSCADOR */}

                <div className="admin-search">
                  <Search />

                  <Form.Control
                    type="text"
                    placeholder="Buscar por nombre, código o marca..."
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                  />
                </div>

                {/* =================================================
                    TABLA
                ================================================= */}

                {cargandoProductos ? (
                  <div className="admin-productos-loading-list">
                    <Spinner animation="border" size="sm" />

                    <span>Cargando productos...</span>
                  </div>
                ) : (
                  <div className="admin-productos-table">
                    <Table hover responsive className="align-middle">
                      <thead>
                        <tr>
                          <th>Imagen</th>

                          <th>Código</th>

                          <th>Producto</th>

                          <th>Marca</th>

                          <th className="text-center">Acciones</th>
                        </tr>
                      </thead>

                      <tbody>
                        {productosFiltrados.length === 0 ? (
                          <tr>
                            <td
                              colSpan="5"
                              className="text-center py-5 text-muted"
                            >
                              No hay productos para mostrar.
                            </td>
                          </tr>
                        ) : (
                          productosFiltrados.map((producto) => (
                            <tr key={producto._id}>
                              {/* IMAGEN */}

                              <td>
                                <img
                                  className="admin-tabla-imagen"
                                  src={producto.imagen}
                                  alt={producto.nombre || "Producto"}
                                />
                              </td>

                              {/* CÓDIGO */}

                              <td>
                                <strong>
                                  {producto.codigoProducto || "—"}
                                </strong>
                              </td>

                              {/* PRODUCTO */}

                              <td>
                                <div className="admin-product-name">
                                  {producto.nombre || "Sin nombre"}
                                </div>

                                {producto.modelo && (
                                  <small>Modelo: {producto.modelo}</small>
                                )}
                              </td>

                              {/* MARCA */}

                              <td>{producto.marca || "—"}</td>

                              {/* ACCIONES */}

                              <td>
                                <div className="d-flex justify-content-center gap-2">
                                  <Button
                                    type="button"
                                    variant="outline-primary"
                                    size="sm"
                                    title="Editar producto"
                                    onClick={() => editarProducto(producto)}
                                  >
                                    <PencilSquare />
                                  </Button>

                                  <Button
                                    type="button"
                                    variant="outline-danger"
                                    size="sm"
                                    title="Eliminar producto"
                                    onClick={() => confirmarEliminar(producto)}
                                  >
                                    <Trash />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* ===================================================
          MODAL ELIMINAR
      =================================================== */}

      <Modal
        show={Boolean(productoEliminar)}
        onHide={cerrarEliminar}
        centered
        backdrop={eliminando ? "static" : true}
        keyboard={!eliminando}
      >
        <Modal.Header closeButton={!eliminando}>
          <Modal.Title>Eliminar producto</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {productoEliminar && (
            <>
              {/* IMAGEN */}

              {productoEliminar.imagen && (
                <div className="text-center mb-3">
                  <img
                    src={productoEliminar.imagen}
                    alt={productoEliminar.nombre || "Producto"}
                    style={{
                      width: "90px",
                      height: "90px",
                      objectFit: "contain",
                      border: "1px solid #dee2e6",
                      borderRadius: "10px",
                      padding: "6px",
                      backgroundColor: "#fff",
                    }}
                  />
                </div>
              )}

              <p className="mb-2">
                ¿Está seguro de eliminar el siguiente producto?
              </p>

              <div className="border rounded p-3 bg-light">
                <strong>{productoEliminar.nombre || "Sin nombre"}</strong>

                <div className="small text-muted mt-1">
                  Código: {productoEliminar.codigoProducto || "—"}
                </div>

                {productoEliminar.marca && (
                  <div className="small text-muted">
                    Marca: {productoEliminar.marca}
                  </div>
                )}

                {productoEliminar.modelo && (
                  <div className="small text-muted">
                    Modelo: {productoEliminar.modelo}
                  </div>
                )}
              </div>

              <Alert variant="warning" className="mt-3 mb-0">
                Se eliminará del catálogo, MongoDB, archivo JSON y la imagen
                guardada en el servidor.
              </Alert>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="light"
            onClick={cerrarEliminar}
            disabled={eliminando}
          >
            Cancelar
          </Button>

          <Button
            variant="danger"
            onClick={eliminarProducto}
            disabled={eliminando}
          >
            {eliminando ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Eliminando...
              </>
            ) : (
              <>
                <Trash className="me-2" />
                Eliminar
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
